/**
 * useTutorCloseOutRecorder — l'enregistrement d'un vocal du bilan, sur place, dans la carte qui le
 * demande : un seul enregistreur (`engine/voice/useVoiceRecorder`) pour toute la page, posé sur la
 * carte visée (`state.recorder.target`).
 *
 * Le geste de l'ancien bilan (`AudioRecordingContainer` dans `StudentCloseOutCard`), un seul toucher
 * de bout en bout : « Enregistrer un vocal » enregistre aussitôt — la carte devient l'enregistreur —,
 * « Terminer » rend le fichier, qui part tout de suite (`state.recorder.onSend`, l'envoi et ses
 * mesures, inchangés) ; « Annuler » abandonne sans rien envoyer. Réenregistrer reprend le même geste :
 * le vocal envoyé est remplacé par le suivant, comme l'ancien bilan qui le supprimait d'abord.
 *
 * Seul « Terminer » envoie. Un enregistrement que le moteur arrête seul — l'app passée en arrière-plan,
 * les 12 minutes atteintes — reste dans la carte avec sa phrase : le tuteur l'envoie ou le refait.
 * Un seul enregistrement à la fois : `open` ne lance rien tant qu'un autre court. Et on ne quitte pas
 * l'écran sur un enregistrement ouvert (retour Android, retour du navigateur) : il faut le finir ou
 * l'annuler ; la page désactive ses propres sorties.
 *
 * Plus de feuille (`<VoiceRecorderSheet>`) : trois gestes là où l'ancien bilan en demandait un
 * (Wajih, 03/11).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useRef } from "react"
import { Linking, Platform } from "react-native"

import { useVoiceRecorder } from "../../../../../../engine/voice/useVoiceRecorder"
import { type VoiceFile } from "../../../../../../engine/voice/voiceOutbox"
import { type InlineVoiceRecorderProps } from "../../../../../../design-system/organisms/InlineVoiceRecorder"
import { type CloseOutTarget } from "../../../../view-models/tutor/Lives/tutorCloseOutDrafts"

/** Un compte-rendu dure 3 s au moins (`minRecordingDurationMs` de l'ancien enregistreur). */
export const CLOSE_OUT_MIN_VOICE_MS = 3000
const TOO_SHORT = "Ton vocal doit durer au moins 3 secondes : continue encore un peu."

/** La navigation, pour la seule garde de sortie. */
type GuardedNavigation = {
    addListener: (event: "beforeRemove", listener: (event: { preventDefault: () => void }) => void) => () => void
}

/** Ce que `useTutorCloseOutState` rend de son enregistreur : la carte visée, l'envoi, la fermeture. */
type RecorderSlot = { target: CloseOutTarget; onSend: (file: VoiceFile) => void; onClose: () => void } | null

export function useTutorCloseOutRecorder(slot: RecorderSlot) {
    const recorder = useVoiceRecorder({ minDurationMs: CLOSE_OUT_MIN_VOICE_MS })
    const slotRef = useRef(slot)
    slotRef.current = slot

    /** Relance l'enregistrement de la carte ouverte : à appeler dans le toucher (le navigateur n'ouvre le micro que là). */
    const start = useCallback(() => void recorder.start(), [recorder])

    /**
     * « Enregistrer un vocal » d'une carte : `designate` la vise (la mesure d'ouverture de l'état du
     * bilan), puis l'enregistrement part. Rien tant qu'un autre enregistrement est ouvert.
     */
    const open = useCallback(
        (designate: () => void) => {
            if (slotRef.current != null) return
            designate()
            start()
        },
        [start]
    )

    const send = useCallback(() => {
        const current = slotRef.current
        const { uri, mimeType, extension, durationMs } = recorder
        if (current == null || uri == null || mimeType == null || extension == null) return
        current.onSend({ uri, mimeType, extension, durationMs })
        void recorder.reset()
        current.onClose()
    }, [recorder])

    const cancel = useCallback(() => {
        void recorder.reset()
        slotRef.current?.onClose()
    }, [recorder])

    // « Terminer » : le fichier fini part aussitôt, puis l'enregistreur revient au repos. Un arrêt du
    // moteur (arrière-plan, durée maximale) porte une phrase (`notice`) : il attend le tuteur.
    const finishedByUser = recorder.state === "recorded" && recorder.uri != null && recorder.notice == null
    const sendRef = useRef(send)
    sendRef.current = send
    useEffect(() => {
        if (finishedByUser) sendRef.current()
    }, [finishedByUser])

    const settings = recorder.state === "denied" && !recorder.canAskAgain && Platform.OS !== "web"
    const target = slot?.target ?? null
    const props: (InlineVoiceRecorderProps & { targetId: string }) | null =
        target == null
            ? null
            : {
                  targetId: target.kind === "student" ? target.id : "platform",
                  phase: recorder.state,
                  durationMs: recorder.durationMs,
                  runningSince: recorder.runningSince,
                  maxDurationMs: recorder.maxDurationMs,
                  notice: recorder.notice,
                  tooShortMessage: TOO_SHORT,
                  stopping: recorder.stopping,
                  onPause: recorder.pause,
                  onResume: recorder.resume,
                  onStop: () => void recorder.stop(),
                  onCancel: cancel,
                  onRetry: start,
                  onSend: send,
                  onRedo: start,
                  onOpenSettings: settings ? () => void Linking.openSettings() : undefined,
              }

    // Une sortie de l'écran (retour Android, retour du navigateur) attend l'enregistrement ouvert.
    const navigation = useNavigation() as unknown as GuardedNavigation
    const active = slot != null
    useEffect(() => {
        if (!active) return undefined
        return navigation.addListener("beforeRemove", (event) => event.preventDefault())
    }, [navigation, active])

    return { open, props }
}
